import { Game } from "../model/Game";
import { Piece } from "../model/Piece";
import { PieceType } from "../model/PieceType";
import { Position } from "../model/Position";

export interface ParsedLevel {
  level: number;
  name: string | null;
  game: Game;
  minimumMoves: number | null;
  escapePoint: Position;
}

export type JsonTextLoader = (filePath: string) => Promise<string>;
type JsonRecord = Record<string, unknown>;
type Pair = readonly [number, number];

const PIECE_TYPES: ReadonlyMap<string, PieceType> = new Map([
  ["1,1", PieceType.UNIT],
  ["1,2", PieceType.LARGE],
  ["2,1", PieceType.LONG],
  ["2,2", PieceType.SQUARE],
]);

export async function loadLevels(
  filePath: string,
  loadText: JsonTextLoader = loadFromBrowser,
): Promise<ParsedLevel[]> {
  try {
    const path = getStaticFilePath(filePath);
    const data: unknown = JSON.parse(await loadText(path));
    return parseLevels(data);
  } catch (error: unknown) {
    const reason = error instanceof Error ? error.message : String(error);
    console.error(`Unable to read levels from "${filePath}": ${reason}`);
    return [];
  }
}

export function loadBrowserLevels(filePath: string): Promise<ParsedLevel[]> {
  return loadLevels(filePath);
}

function getStaticFilePath(filePath: string): string {
  const path = filePath
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^static\//, "");
  const parts = path.split("/");
  if (
    path.length === 0 ||
    parts.some((part) => part === "" || part === "." || part === "..")
  ) {
    throw new Error(
      "The level file path must point inside the static directory",
    );
  }
  return path;
}

const loadFromBrowser: JsonTextLoader = async (
  filePath: string,
): Promise<string> => {
  const response = await fetch(filePath);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} while loading static/${filePath}`);
  }
  return response.text();
};

export function parseLevels(data: unknown): ParsedLevel[] {
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("The JSON root must be a non-empty array of levels");
  }
  const levels = data.map((value: unknown, index: number) =>
    parseLevel(value, index),
  );
  if (new Set(levels.map((level) => level.level)).size !== levels.length) {
    throw new Error("Level numbers must be unique");
  }
  return levels.sort((left, right) => left.level - right.level);
}

function parseLevel(value: unknown, index: number): ParsedLevel {
  const record = asRecord(value, `Level at index ${index}`);
  const level = asPositiveInteger(
    record.level,
    `Level at index ${index}.level`,
  );
  const name = asOptionalName(record.name, `Level ${level}.name`);
  const minimumMoves = asMinimumMoves(
    record.minimumMoves,
    `Level ${level}.minimumMoves`,
  );
  const [height, width] = asPair(
    record.boardSize,
    `Level ${level}.boardSize`,
    true,
  );
  const [escapeY, escapeX] = asPair(
    record.escapePoint,
    `Level ${level}.escapePoint`,
    false,
  );
  if (!Array.isArray(record.blocks) || record.blocks.length === 0) {
    throw new Error(`Level ${level}.blocks must be a non-empty array`);
  }

  const pieces = record.blocks.map((block: unknown, blockIndex: number) =>
    parsePiece(block, level, blockIndex),
  );
  if (pieces.filter((piece) => piece.isMain).length !== 1) {
    throw new Error(`Level ${level} must contain exactly one 2x2 main piece`);
  }
  validateGeometry(pieces, width, height, level);
  if (escapeX + 1 >= width || escapeY + 1 >= height) {
    throw new Error(
      `Level ${level}.escapePoint places the main piece outside the board`,
    );
  }
  return {
    level,
    name,
    game: new Game(pieces, width, height),
    minimumMoves,
    escapePoint: new Position(escapeX, escapeY),
  };
}

function parsePiece(value: unknown, level: number, index: number): Piece {
  const label = `Level ${level}.blocks[${index}]`;
  const block = asRecord(value, label);
  const [shapeHeight, shapeWidth] = asPair(block.shape, `${label}.shape`, true);
  const [positionY, positionX] = asPair(
    block.position,
    `${label}.position`,
    false,
  );
  const pieceType = PIECE_TYPES.get(`${shapeHeight},${shapeWidth}`);
  if (pieceType === undefined) {
    throw new Error(`${label}.shape is not supported`);
  }
  return new Piece(
    new Position(positionX, positionY),
    pieceType,
    pieceType === PieceType.SQUARE,
  );
}

function validateGeometry(
  pieces: Piece[],
  width: number,
  height: number,
  level: number,
): void {
  const occupied = new Set<string>();
  pieces.forEach((piece, pieceIndex) =>
    piece.positions.forEach((position) => {
      if (
        position.x < 0 ||
        position.x >= width ||
        position.y < 0 ||
        position.y >= height
      ) {
        throw new Error(
          `Level ${level}.blocks[${pieceIndex}] exceeds the board`,
        );
      }
      const key = `${position.x},${position.y}`;
      if (occupied.has(key)) {
        throw new Error(
          `Level ${level}.blocks[${pieceIndex}] overlaps another piece`,
        );
      }
      occupied.add(key);
    }),
  );
}

function isJsonRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown, label: string): JsonRecord {
  if (!isJsonRecord(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value;
}

function asPair(value: unknown, label: string, positive: boolean): Pair {
  if (!Array.isArray(value) || value.length !== 2) {
    throw new Error(`${label} must contain exactly two integers`);
  }
  const first: unknown = value[0];
  const second: unknown = value[1];
  const valid = (item: unknown): item is number =>
    typeof item === "number" &&
    Number.isInteger(item) &&
    (positive ? item > 0 : item >= 0);
  if (!valid(first) || !valid(second)) {
    throw new Error(
      `${label} must contain ${positive ? "positive" : "non-negative"} integers`,
    );
  }
  return [first, second];
}

function asOptionalName(value: unknown, label: string): string | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${label} must be a non-empty string`);
  }
  return value.trim();
}

function asMinimumMoves(value: unknown, label: string): number | null {
  if (value === null) {
    return null;
  }
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    throw new Error(`${label} must be null or a positive integer`);
  }
  return value;
}

function asPositiveInteger(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    throw new Error(`${label} must be a positive integer`);
  }
  return value;
}
